/**
 * PlacementConfirmDialog.tsx
 * Warn-and-confirm gate for scheduling a booking onto a contested slot.
 *
 * Shared by the Scenario-Planning drag (Phase1MachineGrid) and the top-table
 * Start editor (Phase1TaskRow) so both surfaces behave identically: when a
 * placement overlaps another booking and/or starts on a weekend/holiday, this
 * dialog explains why and offers three choices —
 *   Cancel            — abandon the move (the bar/date reverts).
 *   Use earliest free  — snap to the earliest free working-day start (optional).
 *   Book anyway        — proceed and create the overlap / weekend booking.
 *
 * Theme tokens (no hex literals):
 *   theme.schedule.conflictHard — red, used when the placement overlaps a booking
 *   theme.schedule.conflictSoft — orange, used for a weekend/holiday-only warning
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { WarningAmber, EventBusy, Block } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** 'YYYY-MM-DD' → 'Mon Jun 22, 2026' (UTC, weekday-aware). */
function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-CA', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PlacementConfirmDialogProps {
  open: boolean;
  /** The placement overlaps another active booking on the machine. */
  conflict: boolean;
  /** The placement starts on a weekend or stat holiday. */
  weekend: boolean;
  /** Human label for the booking it overlaps (task + dates), when a conflict. */
  conflictLabel?: string | null;
  /** Earliest free working-day start, offered as a one-click alternative. */
  earliestFree?: string | null;
  /** Optional context line, e.g. "UCS with Modulus → RK-TX01". */
  detail?: string;
  /** Book anyway — proceed with the contested placement (force). */
  onConfirm: () => void;
  /** Snap to `earliestFree` instead. Hidden when not provided. */
  onUseEarliest?: () => void;
  /** Cancel — revert the move. */
  onCancel: () => void;
  /** Whether a confirm/earliest action is in-flight. */
  saving?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PlacementConfirmDialog: React.FC<PlacementConfirmDialogProps> = ({
  open,
  conflict,
  weekend,
  conflictLabel,
  earliestFree,
  detail,
  onConfirm,
  onUseEarliest,
  onCancel,
  saving = false,
}) => {
  const theme = useTheme();
  // Overlap is the harder barrier → red; a weekend-only warning is orange.
  const accent = conflict ? theme.schedule.conflictHard : theme.schedule.conflictSoft;

  const title = conflict && weekend
    ? 'Overlap + weekend start'
    : conflict
      ? 'This overlaps another booking'
      : 'Weekend / holiday start';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${theme.border.radius}px`, border: `1px solid ${accent}` } }}
    >
      <DialogTitle
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1,
          borderBottom: `1px solid ${theme.border.color}`,
        }}
      >
        <WarningAmber sx={{ color: accent, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.25}>
            {title}
          </Typography>
          {detail && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {detail}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {conflict && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Block sx={{ color: theme.schedule.conflictHard, fontSize: 18, mt: 0.1 }} />
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                The machine already has a booking in this date range. Proceeding will
                double-book it — the overlap will show as a hard conflict in the
                Conflicts panel.
                {conflictLabel && (
                  <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.78rem' }}>
                    Conflicts with: <strong>{conflictLabel}</strong>.
                    {' '}If it belongs to another plan it is drawn here as a grey
                    {' '}&ldquo;Reserved in another plan&rdquo; bar, and on the Machine Schedule /
                    {' '}Master Gantt as a hatched proposal.
                  </Box>
                )}
              </Typography>
            </Box>
          )}
          {weekend && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <EventBusy sx={{ color: theme.schedule.conflictSoft, fontSize: 18, mt: 0.1 }} />
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                This starts on a weekend or stat holiday — normally closed. Continue
                only if the lab is genuinely working that day.
              </Typography>
            </Box>
          )}
          {earliestFree && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.73rem' }}>
              Earliest free working-day start: <strong>{fmtDate(earliestFree)}</strong>.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${theme.border.color}`, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onCancel} disabled={saving} size="small" variant="outlined" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {earliestFree && onUseEarliest && (
          <Button
            onClick={onUseEarliest}
            disabled={saving}
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            {conflict ? `Use earliest free (${fmtDate(earliestFree)})` : `Use next weekday (${fmtDate(earliestFree)})`}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          disabled={saving}
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} /> : <WarningAmber />}
          sx={{
            textTransform: 'none',
            backgroundColor: accent,
            '&:hover': { backgroundColor: alpha(accent, 0.85) },
            '&.Mui-disabled': { backgroundColor: alpha(accent, 0.4) },
          }}
        >
          {saving ? 'Saving…' : 'Book anyway'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
